import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { settingsApi } from '@/lib/api';
import { Menu, X, ChevronDown, User, LogOut, Settings, Church, Facebook, Youtube, Phone, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  useEffect(() => {
    settingsApi.get().then(setSiteSettings).catch(() => {});
  }, []);

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'Sermons', href: '/sermons' },
    { name: 'About', href: '/about' },
    { name: 'Events', href: '/events' },
    { name: 'Prayer Requests', href: '/prayer-requests' },
    { name: 'Galleries', href: '/galleries' },
    { name: 'Contact', href: '/contact' },
    { name: 'Gingerbread School', href: 'http://stjohnsgingerbreadschool.org/', external: true },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top Bar */}
      <div className="bg-amber-800 text-white py-2 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-sm">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
            <span className="flex items-center">
              <Phone className="h-4 w-4 mr-1 shrink-0" />
              {siteSettings?.phone || '219-663-1608'}
            </span>
            {Array.isArray(siteSettings?.service_times) ? (
              siteSettings.service_times.map((time, i) => (
                <span key={i} className="hidden sm:inline" data-testid={`topbar-service-time-${i}`}>{time}</span>
              ))
            ) : (
              <span className="hidden sm:inline" data-testid="topbar-service-times">{siteSettings?.service_times || 'Sunday Worship: 10:00 AM'}</span>
            )}
          </div>
          <div className="flex items-center space-x-3">
            {siteSettings?.facebook_url && (
              <a href={siteSettings.facebook_url} target="_blank" rel="noopener noreferrer" 
                 className="hover:text-amber-200 transition-colors" data-testid="topbar-facebook-link">
                <Facebook className="h-4 w-4" />
              </a>
            )}
            {siteSettings?.youtube_url && (
              <a href={siteSettings.youtube_url} target="_blank" rel="noopener noreferrer" 
                 className="hover:text-amber-200 transition-colors" data-testid="topbar-youtube-link">
                <Youtube className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <Church className="h-10 w-10 text-amber-700" />
              <div>
                <h1 className="text-xl font-serif font-bold text-stone-800">St. John's UCC</h1>
                <p className="text-xs text-stone-500">Crown Point, IN</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                item.external ? (
                  <a
                    key={item.name}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-2 rounded-md text-sm font-medium text-stone-600 hover:text-amber-700 hover:bg-amber-50 transition-colors flex items-center"
                  >
                    {item.name}
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.href)
                        ? 'text-amber-700 bg-amber-50'
                        : 'text-stone-600 hover:text-amber-700 hover:bg-amber-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-3">
              <Link to="/give">
                <Button className="bg-amber-700 hover:bg-amber-800 text-white hidden sm:flex">
                  Give Online
                </Button>
              </Link>
              
              <Link to="/live">
                <Button variant="outline" className="border-red-500 text-red-500 hover:bg-red-50 hidden sm:flex">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
                  Live
                </Button>
              </Link>

              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center space-x-2">
                      {user.profile_image || user.picture ? (
                        <img src={user.profile_image || user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
                      ) : (
                        <User className="h-5 w-5" />
                      )}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <div className="px-2 py-1.5 text-sm font-medium">{user.name}</div>
                    <div className="px-2 pb-1.5 text-xs text-stone-500">{user.email}</div>
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center">
                        <User className="h-4 w-4 mr-2" />
                        My Profile
                      </Link>
                    </DropdownMenuItem>
                    {isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center">
                          <Settings className="h-4 w-4 mr-2" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {(user.role === 'member' || user.role === 'admin') && (
                      <DropdownMenuItem asChild>
                        <Link to="/members" className="flex items-center">
                          <User className="h-4 w-4 mr-2" />
                          Member Directory
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={logout} className="text-red-600">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link to="/login">
                  <Button variant="ghost" className="hidden sm:flex">
                    <User className="h-5 w-5 mr-2" />
                    Login
                  </Button>
                </Link>
              )}

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-stone-600" />
                ) : (
                  <Menu className="h-6 w-6 text-stone-600" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="lg:hidden pb-4">
              <nav className="flex flex-col space-y-1">
                {navigation.map((item) => (
                  item.external ? (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMobileMenuOpen(false)}
                      className="px-3 py-2 rounded-md text-sm font-medium text-stone-600 hover:text-amber-700 hover:bg-amber-50 flex items-center"
                    >
                      {item.name}
                      <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        isActive(item.href)
                          ? 'text-amber-700 bg-amber-50'
                          : 'text-stone-600 hover:text-amber-700 hover:bg-amber-50'
                      }`}
                    >
                      {item.name}
                    </Link>
                  )
                ))}
                <Link
                  to="/give"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-3 py-2 text-amber-700 font-medium"
                >
                  Give Online
                </Link>
                <Link
                  to="/live"
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-3 py-2 text-red-500 font-medium flex items-center"
                >
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
                  Watch Live
                </Link>
                {!user && (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-3 py-2 text-stone-600 font-medium"
                  >
                    Login
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-stone-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Church Info */}
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <Church className="h-8 w-8 text-amber-400" />
                <div>
                  <h3 className="text-lg font-serif font-bold">St. John's United Church of Christ</h3>
                  <p className="text-sm text-stone-400">Good People Serving a GREAT GOD!</p>
                </div>
              </div>
              <p className="text-stone-300 text-sm mb-4">
                We are compassionate followers of Jesus Christ doing our best to serve as God leads 
                with the needs of our members, families and community.
              </p>
              <div className="flex space-x-4">
                {siteSettings?.facebook_url && (
                  <a href={siteSettings.facebook_url} target="_blank" rel="noopener noreferrer"
                     className="text-stone-400 hover:text-amber-400 transition-colors" data-testid="footer-facebook-link">
                    <Facebook className="h-6 w-6" />
                  </a>
                )}
                {siteSettings?.youtube_url && (
                  <a href={siteSettings.youtube_url} target="_blank" rel="noopener noreferrer"
                     className="text-stone-400 hover:text-amber-400 transition-colors" data-testid="footer-youtube-link">
                    <Youtube className="h-6 w-6" />
                  </a>
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-semibold mb-4 text-amber-400">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/sermons" className="text-stone-300 hover:text-white">Sermons</Link></li>
                <li><Link to="/events" className="text-stone-300 hover:text-white">Events</Link></li>
                <li><Link to="/prayer-requests" className="text-stone-300 hover:text-white">Prayer Requests</Link></li>
                <li><Link to="/give" className="text-stone-300 hover:text-white">Give Online</Link></li>
                <li><Link to="/contact" className="text-stone-300 hover:text-white">Contact Us</Link></li>
                <li>
                  <a href="http://stjohnsgingerbreadschool.org/" target="_blank" rel="noopener noreferrer" 
                     className="text-stone-300 hover:text-white flex items-center">
                    Gingerbread School <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold mb-4 text-amber-400">Contact</h4>
              <address className="text-sm text-stone-300 not-italic space-y-2">
                <p>{siteSettings?.address || '1288 S. Indiana Ave, Crown Point, IN 46307'}</p>
                <p className="mt-2">
                  <a href={`tel:${siteSettings?.phone || '219-663-1608'}`} className="hover:text-white">{siteSettings?.phone || '219-663-1608'}</a>
                </p>
                <p>
                  <a href={`mailto:${siteSettings?.email || 'cpuccsecretary@gmail.com'}`} className="hover:text-white">
                    {siteSettings?.email || 'cpuccsecretary@gmail.com'}
                  </a>
                </p>
              </address>
            </div>
          </div>

          <div className="border-t border-stone-700 mt-8 pt-8 text-center text-sm text-stone-400">
            <p>&copy; {new Date().getFullYear()} St. John's United Church of Christ. All rights reserved.</p>
            <p className="mt-2">
              <a href="https://app.emergent.sh" target="_blank" rel="noopener noreferrer" className="text-stone-500 hover:text-stone-300 transition-colors">
                Made with Emergent
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
